import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  RateReview as FeedbackIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAllFeedback,
  updateFeedbackStatus,
} from '../services/betaFeedbackService';
import {
  CATEGORY_LABELS,
  FeedbackCategory,
  FeedbackStatus,
  IBetaFeedback,
  STATUS_COLORS,
  STATUS_LABELS,
} from '../types/betaFeedback';

const ALL = 'all';

const FeedbackManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [category, setCategory] = useState<FeedbackCategory | typeof ALL>(ALL);
  const [status, setStatus] = useState<FeedbackStatus | typeof ALL>(ALL);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageTitle, setSelectedImageTitle] = useState('');
  const [adminNotesById, setAdminNotesById] = useState<Record<string, string>>({});

  const filters = {
    ...(category !== ALL ? { category } : {}),
    ...(status !== ALL ? { status } : {}),
    limit: 100,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['feedbackManagement', filters],
    queryFn: () => getAllFeedback(filters),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({
      id,
      nextStatus,
      adminNotes,
    }: {
      id: string;
      nextStatus: FeedbackStatus;
      adminNotes?: string;
    }) => updateFeedbackStatus(id, nextStatus, adminNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedbackManagement'] });
    },
  });

  const feedback = data?.feedback ?? [];

  const counts = feedback.reduce(
    (acc, item) => {
      acc[item.status] += 1;
      return acc;
    },
    { open: 0, in_progress: 0, resolved: 0 } as Record<FeedbackStatus, number>
  );

  const handleImageOpen = (url: string, fileName: string) => {
    setSelectedImage(url);
    setSelectedImageTitle(fileName);
  };

  const handleStatusChange = (item: IBetaFeedback, nextStatus: FeedbackStatus) => {
    updateStatusMutation.mutate({
      id: item._id,
      nextStatus,
      adminNotes: adminNotesById[item._id],
    });
  };

  return (
    <Box>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1f2937 0%, #4c1d95 100%)',
          borderRadius: 3,
          p: 4,
          mb: 3,
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <AdminIcon sx={{ fontSize: 36 }} />
          <Typography variant="h4" fontWeight={700}>
            Feedback Review
          </Typography>
          <Chip
            label="ROOT"
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 700 }}
          />
        </Box>
        <Typography sx={{ opacity: 0.9, maxWidth: 720 }}>
          Review feedback from all users, prioritise issues, and inspect
          screenshots attached to bug reports or non-functional concerns.
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Open
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {counts.open}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="body2" color="text.secondary">
              In Progress
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {counts.in_progress}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Resolved
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {counts.resolved}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={category}
              onChange={(event) => setCategory(event.target.value as FeedbackCategory | typeof ALL)}
            >
              <MenuItem value={ALL}>All Categories</MenuItem>
              {(Object.entries(CATEGORY_LABELS) as [FeedbackCategory, string][]).map(
                ([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(event) => setStatus(event.target.value as FeedbackStatus | typeof ALL)}
            >
              <MenuItem value={ALL}>All Statuses</MenuItem>
              {(Object.entries(STATUS_LABELS) as [FeedbackStatus, string][]).map(
                ([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {(error as any)?.response?.data?.error || 'Failed to load feedback.'}
        </Alert>
      )}

      {!isLoading && !isError && feedback.length === 0 && (
        <Paper sx={{ p: 5, borderRadius: 3, textAlign: 'center' }}>
          <FeedbackIcon sx={{ fontSize: 52, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" fontWeight={600}>
            No feedback found
          </Typography>
          <Typography color="text.secondary">
            Try changing the filters or check again after users submit feedback.
          </Typography>
        </Paper>
      )}

      <Stack spacing={2}>
        {feedback.map((item) => (
          <Paper key={item._id} sx={{ p: 2.5, borderRadius: 3 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Chip label={CATEGORY_LABELS[item.category]} size="small" />
                    <Chip
                      label={STATUS_LABELS[item.status]}
                      size="small"
                      color={STATUS_COLORS[item.status]}
                    />
                  </Stack>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {item.userName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.userEmail} • {new Date(item.createdAt).toLocaleString('en-AU')}
                  </Typography>
                </Box>

                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={item.status}
                    onChange={(event) =>
                      handleStatusChange(item, event.target.value as FeedbackStatus)
                    }
                    disabled={updateStatusMutation.isPending}
                  >
                    {(Object.entries(STATUS_LABELS) as [FeedbackStatus, string][]).map(
                      ([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Box>

              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{item.description}</Typography>

              {item.attachments.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Screenshots
                  </Typography>
                  <Grid container spacing={1.5}>
                    {item.attachments.map((attachment) => (
                      <Grid item xs={12} sm={6} md={4} key={attachment.url}>
                        <Box
                          role="button"
                          tabIndex={0}
                          onClick={() => handleImageOpen(attachment.url, attachment.fileName)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              handleImageOpen(attachment.url, attachment.fileName);
                            }
                          }}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            bgcolor: 'grey.50',
                          }}
                        >
                          <Box
                            component="img"
                            src={attachment.url}
                            alt={attachment.fileName}
                            sx={{
                              width: '100%',
                              height: 160,
                              objectFit: 'cover',
                              display: 'block',
                            }}
                          />
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1 }}>
                            <ImageIcon fontSize="small" color="action" />
                            <Typography variant="caption" noWrap>
                              {attachment.fileName}
                            </Typography>
                          </Stack>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              <TextField
                label="Admin notes"
                multiline
                minRows={2}
                value={adminNotesById[item._id] ?? item.adminNotes ?? ''}
                onChange={(event) =>
                  setAdminNotesById((prev) => ({
                    ...prev,
                    [item._id]: event.target.value,
                  }))
                }
                placeholder="Add internal notes or a team response..."
              />
              <Box>
                <Button
                  variant="outlined"
                  onClick={() => handleStatusChange(item, item.status)}
                  disabled={updateStatusMutation.isPending}
                >
                  Save Notes
                </Button>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {selectedImageTitle}
          </Typography>
          <IconButton onClick={() => setSelectedImage(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt={selectedImageTitle}
              sx={{
                width: '100%',
                maxHeight: '75vh',
                objectFit: 'contain',
                display: 'block',
                bgcolor: 'grey.100',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FeedbackManagement;
